import React, { useState, useMemo } from "react";
import { Search, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { MENU, IMAGES, RESTAURANT } from "../data/menu";

export default function MenuPage() {
  const [active, setActive] = useState(MENU[0].category);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return MENU;
    const q = query.toLowerCase();
    return MENU.map((section) => ({
      ...section,
      items: section.items.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          (i.desc && i.desc.toLowerCase().includes(q))
      ),
    })).filter((s) => s.items.length > 0);
  }, [query]);

  return (
    <main className="bg-[#0A0A0A] min-h-screen pt-[180px]" data-testid="menu-page">
      {/* Header */}
      <section className="relative py-12 md:py-16 overflow-hidden" data-testid="menu-header">
        <div className="absolute inset-0">
          <img src={IMAGES.plating} alt="Menu" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-[#D4A017] uppercase tracking-[0.3em] text-xs">The Menu</span>
          <h1 className="font-impact uppercase text-white text-5xl md:text-7xl mt-3 leading-[0.95] tracking-tight max-w-3xl">
            A taste of <span className="text-[#D4A017]">Nigeria</span>, on every plate.
          </h1>
          <p className="text-white/65 mt-5 max-w-2xl text-lg">
            Browse our full digital menu. To order, give us a call — our team will be delighted to help.
          </p>
          <a
            href={`tel:${RESTAURANT.phoneRaw}`}
            data-testid="menu-call-cta"
            className="mt-6 inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#F0BE3F] text-black font-bold uppercase tracking-[0.12em] px-6 py-3 text-xs rounded-full transition-colors shadow-md shadow-[#D4A017]/20"
          >
            <Phone size={14} /> Call {RESTAURANT.phone}
          </a>
        </div>
      </section>

      {/* Sticky category nav + search */}
      <div
        className="sticky top-[170px] z-40 bg-[#0A0A0A]/95 backdrop-blur-md border-y border-white/10"
        data-testid="menu-sticky-nav"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center gap-4">
          <div className="flex-1 overflow-x-auto no-scrollbar">
            <div className="flex gap-2 whitespace-nowrap">
              {MENU.map((s) => (
                <button
                  key={s.category}
                  onClick={() => {
                    setActive(s.category);
                    document
                      .getElementById(`section-${s.category.replace(/\s+/g, "-")}`)
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                    active === s.category
                      ? "bg-[#D4A017] text-black border-[#D4A017]"
                      : "border-white/15 text-white/70 hover:border-[#D4A017] hover:text-[#D4A017]"
                  }`}
                  data-testid={`menu-category-${s.category.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {s.category}
                </button>
              ))}
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 border border-white/15 px-3 py-2">
            <Search size={16} className="text-white/50" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search dishes"
              className="bg-transparent text-sm text-white placeholder:text-white/40 focus:outline-none w-44"
              data-testid="menu-search-input"
            />
          </div>
        </div>
      </div>

      {/* Menu sections */}
      <section id="order" className="py-16 md:py-24" data-testid="menu-sections">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {filtered.length === 0 && (
            <div className="text-center py-20 text-white/50" data-testid="menu-no-results">
              No dishes found for "{query}".
            </div>
          )}
          {filtered.map((section) => (
            <div
              key={section.category}
              id={`section-${section.category.replace(/\s+/g, "-")}`}
              className="mb-20 scroll-mt-40"
              data-testid={`menu-section-${section.category.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-10 pb-6 border-b border-white/10">
                <div>
                  <span className="text-[#D4A017] uppercase tracking-[0.3em] text-xs">
                    Course
                  </span>
                  <h2 className="font-display text-white text-3xl md:text-5xl mt-2">
                    {section.category}
                  </h2>
                </div>
                {section.description && (
                  <p className="text-white/55 text-sm md:text-base italic font-accent max-w-md">
                    {section.description}
                  </p>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-x-16 gap-y-8">
                {section.items.map((item) => (
                  <div
                    key={item.name}
                    className="group flex items-start gap-4 py-3"
                    data-testid={`menu-item-${item.name.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline">
                        <h3 className="font-display text-white text-xl md:text-2xl group-hover:text-[#D4A017] transition-colors">
                          {item.name}
                        </h3>
                        <span className="dotted-leader" />
                        <span className="text-[#D4A017] font-medium text-lg md:text-xl whitespace-nowrap">
                          £{item.price.toFixed(2)}
                        </span>
                      </div>
                      {item.desc && (
                        <p className="text-white/55 text-sm leading-relaxed mt-2 max-w-md">
                          {item.desc}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Order CTA — call us */}
      <section className="py-16 bg-[#0F0F0F] border-t border-white/10" data-testid="menu-ordering">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0F0F0F] border border-[#D4A017]/20 p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="text-[#D4A017] uppercase tracking-[0.3em] text-xs">Ready to order?</span>
              <h3 className="font-impact uppercase text-white text-3xl md:text-4xl mt-3 tracking-wider">
                Give us a call.
              </h3>
              <p className="text-white/65 mt-3">
                Our team will guide you through the menu and take your order over the phone — quick,
                personal, and just how we like it.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <a
                href={`tel:${RESTAURANT.phoneRaw}`}
                className="inline-flex items-center justify-center gap-3 bg-[#D4A017] hover:bg-[#F0BE3F] text-black font-bold uppercase tracking-[0.12em] px-6 py-4 text-sm rounded-full transition-colors"
                data-testid="menu-cta-call"
              >
                <Phone size={16} /> {RESTAURANT.phone}
              </a>
              <Link
                to="/order"
                className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-[#D4A017] text-white hover:text-[#D4A017] font-bold uppercase tracking-[0.12em] px-6 py-3.5 text-sm rounded-full transition-colors"
                data-testid="menu-cta-order-online"
              >
                Or Order via Delivery Partners
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
